import { useState, useRef, useEffect } from 'react';

const TIME_FORMAT_KEY = 'smsscheduler_time_format';

// Get default time format based on current path
const getDefaultTimeFormat = () => {
  const stored = localStorage.getItem(TIME_FORMAT_KEY);
  if (stored) return stored;

  // Default: 12h for en-us, 24h for others
  const path = window.location.pathname;
  return path.includes('/en-us') ? '12h' : '24h';
};

export function TimeInput({
  trads,
  onChange,
  defaultValue,
  hideFormatToggle = false,
  disabled = false,
}) {
  const [hh, setHh] = useState('');
  const [mm, setMm] = useState('');
  const [isPM, setIsPM] = useState(false);
  const [timeFormat, setTimeFormat] = useState(getDefaultTimeFormat());

  // Initialize from defaultValue (minutes since midnight)
  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null) {
      const totalMinutes = Number(defaultValue);
      if (!isNaN(totalMinutes) && totalMinutes >= 0 && totalMinutes < 1440) {
        let h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;

        if (timeFormat === '12h') {
          const pm = h >= 12;
          if (h === 0) h = 12;
          else if (h > 12) h -= 12;
          setIsPM(pm);
        }

        setHh(h.toString().padStart(2, '0'));
        setMm(m.toString().padStart(2, '0'));
      }
    }
  }, [defaultValue, timeFormat]);
  const mmRef = useRef(null);
  const hhRef = useRef(null);

  const notify = (newHh, newMm, newIsPM) => {
    // Allow empty or incomplete input
    if (!newHh || !newMm) {
      onChange?.(undefined);
      return;
    }

    // Pad single digits with leading zero
    const paddedHh = newHh.padStart(2, '0');
    const paddedMm = newMm.padStart(2, '0');

    let h = parseInt(paddedHh, 10);
    const m = parseInt(paddedMm, 10);

    // Validate based on format
    const maxHour = timeFormat === '24h' ? 23 : 12;
    const minHour = timeFormat === '24h' ? 0 : 1;

    if (isNaN(h) || isNaN(m) || h < minHour || h > maxHour || m < 0 || m > 59) {
      onChange?.(undefined);
      return;
    }

    // Convert to 24h for storage
    let h24 = h;
    if (timeFormat === '12h') {
      if (newIsPM) {
        if (h !== 12) h24 = h + 12;
      } else {
        if (h === 12) h24 = 0;
      }
    }

    const result = `${h24.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    onChange?.(result);
  };

  const handleHH = (e) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 2);

    // Clamp based on format
    if (v.length === 2) {
      const n = parseInt(v, 10);
      if (timeFormat === '24h') {
        if (n > 23) v = '23';
      } else {
        if (n > 12) v = '12';
        if (n < 1 && v !== '0' && v !== '00') v = '01';
      }
    }

    setHh(v);
    notify(v, mm, isPM);

    if (v.length === 2) mmRef.current?.focus();
  };

  const handleMM = (e) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 2);

    // Clamp to 59
    if (v.length === 2) {
      const n = parseInt(v, 10);
      if (n > 59) v = '59';
    }

    setMm(v);
    notify(hh, v, isPM);
  };

  const handleMMKeyDown = (e) => {
    if (e.key === 'Backspace' && mm === '') {
      hhRef.current?.focus();
    }
  };

  const toggleAMPM = () => {
    const newIsPM = !isPM;
    setIsPM(newIsPM);
    notify(hh, mm, newIsPM);
  };

  const toggleTimeFormat = () => {
    const newFormat = timeFormat === '12h' ? '24h' : '12h';
    setTimeFormat(newFormat);
    localStorage.setItem(TIME_FORMAT_KEY, newFormat);

    // Convert current time to new format
    if (hh && mm) {
      let h = parseInt(hh, 10);

      if (newFormat === '24h') {
        // Converting from 12h to 24h
        if (isPM && h !== 12) h += 12;
        else if (!isPM && h === 12) h = 0;
      } else {
        // Converting from 24h to 12h
        const pm = h >= 12;
        if (h === 0)
          h = 12; // 00:xx becomes 12:xx AM
        else if (h > 12) h -= 12; // 13:xx becomes 1:xx PM, etc.
        // h === 12 stays as 12 (12:xx PM)
        setIsPM(pm);
      }

      setHh(h.toString().padStart(2, '0'));
      const newIsPM = newFormat === '12h' ? parseInt(hh, 10) >= 12 : isPM;
      notify(h.toString().padStart(2, '0'), mm, newIsPM);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center border border-gray-300 rounded-lg px-3 h-10 bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
        <input
          ref={hhRef}
          type="text"
          inputMode="numeric"
          maxLength={2}
          placeholder="HH"
          value={hh}
          onChange={handleHH}
          disabled={disabled}
          data-testid="time-hh-input"
          className="w-6 text-center outline-none bg-transparent text-lg font-mono disabled:text-gray-400"
        />
        <span className="text-gray-400 text-lg font-mono">:</span>
        <input
          ref={mmRef}
          type="text"
          inputMode="numeric"
          maxLength={2}
          placeholder="MM"
          value={mm}
          onChange={handleMM}
          onKeyDown={handleMMKeyDown}
          disabled={disabled}
          data-testid="time-mm-input"
          className="w-6 text-center outline-none bg-transparent text-lg font-mono disabled:text-gray-400"
        />
      </div>

      {timeFormat === '12h' && (
        <button
          type="button"
          onClick={toggleAMPM}
          disabled={disabled}
          data-testid="time-ampm-btn"
          className="px-3 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 font-medium text-sm min-w-14 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isPM ? 'PM' : 'AM'}
        </button>
      )}

      {!hideFormatToggle && (
        <button
          type="button"
          onClick={toggleTimeFormat}
          disabled={disabled}
          data-testid="time-format-toggle"
          className="px-3 h-10 font-medium text-gray-900 underline hover:text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {timeFormat === '12h'
            ? trads.index.switchTo24h
            : trads.index.switchTo12h}
        </button>
      )}
    </div>
  );
}
